'use client'

import { skipToken, useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import {
  type Abi,
  AbiDecodingDataSizeInvalidError,
  AbiDecodingDataSizeTooSmallError,
  AbiDecodingZeroDataError,
  type Address,
  BaseError,
  CallExecutionError,
  ContractFunctionRevertedError,
  ContractFunctionZeroDataError,
  HttpRequestError,
  InvalidBytesBooleanError,
  InvalidHexBooleanError,
  RpcRequestError,
  TimeoutError,
  zeroAddress
} from 'viem'
import { usePublicClient } from 'wagmi'

import {
  MARKET_GEO_RESTRICTION_MESSAGE,
  RESTRICTED_MARKET_CHAIN_ID,
  RESTRICTED_MARKET_INTERFACE_ID
} from '@/config/marketGeoRestriction'
import { useUtilityContext } from '@/contexts/UtilityContext/Utility.context'
import {
  deduplicateAddresses,
  getMarketGeoRestrictionVerdict,
  type MarketGeoRestrictionProbe,
  shouldProbeMarketGeoRestriction
} from '@/helpers/marketGeoRestriction'
import { logger } from '@/lib/observability/logger'

const ERC165_ABI = [
  {
    type: 'function',
    name: 'supportsInterface',
    stateMutability: 'view',
    inputs: [{ name: 'interfaceId', type: 'bytes4' }],
    outputs: [{ type: 'bool' }]
  }
] as const

const WRAPPER_ABI = [
  {
    type: 'function',
    name: 'asset',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }]
  },
  {
    type: 'function',
    name: 'underlying',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }]
  },
  {
    type: 'function',
    name: 'token',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }]
  }
] as const

const MULTICALL3_ABI = [
  {
    type: 'function',
    name: 'getChainId',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }]
  }
] as const

// One-hour cache and re-probe interval balance RPC load against upgradeable-proxy implementation
// changes. Focus refetching separately recovers probes that failed while the panel stayed mounted.
const MARKET_GEO_RESTRICTION_CACHE_TTL = 60 * 60 * 1000

type PublicClient = NonNullable<ReturnType<typeof usePublicClient>>

/**
 * Probes direct assets, then resolves exactly one wrapper hop. Each multicall includes a Multicall3
 * sentinel as its final contract. The sentinel assertion reads that final result, and the caller
 * strips it before classifying token results.
 *
 * Reverts and non-decodable per-call responses are non-matches. Everything else, including
 * transport, RPC, aggregate3-level, and other infrastructure failures, propagates so the hook can
 * fail closed.
 */
const probeMarketAssets = async (
  publicClient: PublicClient,
  addresses: readonly Address[]
): Promise<boolean> => {
  const multicall3Address = publicClient.chain?.contracts?.multicall3?.address
  if (!multicall3Address) throw new Error('Multicall3 address is unavailable')

  const sentinel = {
    address: multicall3Address,
    abi: MULTICALL3_ABI,
    functionName: 'getChainId'
  } as const

  const runMulticall = async (
    contracts: readonly {
      address: Address
      abi: Abi
      functionName: string
      args?: readonly unknown[]
    }[]
  ) => {
    const results = await publicClient.multicall({
      allowFailure: true,
      contracts: [...contracts, sentinel]
    })
    assertMulticallSentinelSuccess(results)
    const tokenResults = results.slice(0, -1)
    throwOnUnexpectedMulticallFailures(tokenResults)
    return tokenResults
  }

  const directResults = await runMulticall(
    addresses.map(address => ({
      address,
      abi: ERC165_ABI,
      functionName: 'supportsInterface',
      args: [RESTRICTED_MARKET_INTERFACE_ID]
    }))
  )

  if (directResults.some(result => result.status === 'success' && result.result === true)) {
    return true
  }

  const addressSet = new Set(addresses.map(address => address.toLowerCase()))
  const underlyingResults = await runMulticall(
    addresses.flatMap(address =>
      (['asset', 'underlying', 'token'] as const).map(functionName => ({
        address,
        abi: WRAPPER_ABI,
        functionName
      }))
    )
  )
  const underlyingAddresses = deduplicateAddresses(
    underlyingResults
      .filter(
        (result): result is Extract<typeof result, { status: 'success' }> =>
          result.status === 'success'
      )
      .map(result => result.result)
      .filter(
        (address): address is Address =>
          typeof address === 'string' &&
          address !== zeroAddress &&
          !addressSet.has(address.toLowerCase())
      )
  )

  if (underlyingAddresses.length === 0) return false

  const underlyingResultsWithInterface = await runMulticall(
    underlyingAddresses.map(address => ({
      address,
      abi: ERC165_ABI,
      functionName: 'supportsInterface',
      args: [RESTRICTED_MARKET_INTERFACE_ID]
    }))
  )

  return underlyingResultsWithInterface.some(
    result => result.status === 'success' && result.result === true
  )
}

// Membership here is the fail-open/fail-closed boundary: these errors are per-token non-matches.
const CONTRACT_LEVEL_FAILURES = [
  AbiDecodingDataSizeInvalidError,
  AbiDecodingDataSizeTooSmallError,
  AbiDecodingZeroDataError,
  ContractFunctionRevertedError,
  ContractFunctionZeroDataError,
  InvalidBytesBooleanError,
  InvalidHexBooleanError
] as const

// Any transport cause anywhere in the chain overrides contract-looking causes and is fatal.
const TRANSPORT_FAILURES = [
  CallExecutionError,
  RpcRequestError,
  HttpRequestError,
  TimeoutError
] as const

const isContractLevelFailure = (error: unknown): boolean => {
  if (!(error instanceof BaseError)) return false

  let hasContractLevelFailure = false
  let hasTransportFailure = false

  error.walk(cause => {
    hasContractLevelFailure ||= CONTRACT_LEVEL_FAILURES.some(
      ErrorType => cause instanceof ErrorType
    )
    hasTransportFailure ||= TRANSPORT_FAILURES.some(ErrorType => cause instanceof ErrorType)
    return false
  })

  return hasContractLevelFailure && !hasTransportFailure
}

const assertMulticallSentinelSuccess = (
  results: readonly { status: string; error?: unknown; result?: unknown }[]
) => {
  const sentinel = results[results.length - 1]
  if (sentinel?.status === 'success' && sentinel.result === BigInt(RESTRICTED_MARKET_CHAIN_ID)) {
    return
  }
  if (sentinel?.status === 'success') {
    throw new Error('Unexpected multicall sentinel chain id')
  }
  if (sentinel?.error != null) throw sentinel.error
  throw new Error('Unexpected multicall sentinel failure without an error')
}

const throwOnUnexpectedMulticallFailures = (
  results: readonly { status: string; error?: unknown }[]
) => {
  const failure = results.find(
    result => result.status === 'failure' && !isContractLevelFailure(result.error)
  )
  if (!failure) return
  if (failure.error == null) {
    throw new Error('Unexpected multicall failure without an error')
  }
  throw failure.error
}

interface UseMarketGeoRestrictionParams {
  chainId: number | undefined
  assetAddresses: readonly (Address | null | undefined)[]
}

export interface MarketGeoRestriction {
  isRestricted: boolean
  isPending: boolean
  /** Meaningful only when {@link isRestricted} is true. */
  message: string
}

/**
 * Returns an unrestricted verdict without an RPC probe outside restricted countries or on
 * non-Robinhood chains. An unknown country probes like a restricted one. A missing public client
 * or probe error returns a restricted verdict.
 */
export const useMarketGeoRestriction = ({
  chainId,
  assetAddresses
}: UseMarketGeoRestrictionParams): MarketGeoRestriction => {
  const { countryCode } = useUtilityContext()
  const addresses = deduplicateAddresses(assetAddresses)
  // Sorted so the same asset pair in either order shares one cache entry; the probe reduces with
  // `some`, so order never changes the verdict.
  const addressKey = addresses
    .map(address => address.toLowerCase())
    .sort()
    .join(',')
  const shouldProbe = shouldProbeMarketGeoRestriction(countryCode, chainId)
  const publicClient = usePublicClient({ chainId: RESTRICTED_MARKET_CHAIN_ID })

  const probeQuery = useQuery({
    queryKey: ['market-geo-restriction', chainId, addressKey],
    queryFn:
      publicClient && addresses.length > 0
        ? probeMarketAssets.bind(null, publicClient, addresses)
        : skipToken,
    enabled: shouldProbe,
    retry: 1,
    staleTime: MARKET_GEO_RESTRICTION_CACHE_TTL,
    gcTime: MARKET_GEO_RESTRICTION_CACHE_TTL,
    refetchInterval: MARKET_GEO_RESTRICTION_CACHE_TTL
  })

  const probeError =
    probeQuery.error ?? (!publicClient && shouldProbe ? 'missing-client' : undefined)
  const lastLoggedProbeError = useRef<unknown>(undefined)
  useEffect(() => {
    if (!probeError || lastLoggedProbeError.current === probeError) return
    lastLoggedProbeError.current = probeError
    const error =
      probeError === 'missing-client'
        ? new Error('Robinhood public client is unavailable')
        : probeError instanceof Error
          ? probeError
          : new Error(String(probeError))
    logger.error(error, {
      chainId,
      extra: { addressKey }
    })
  }, [addressKey, chainId, probeError])

  const probe: MarketGeoRestrictionProbe = !shouldProbe
    ? { status: 'success', hasRestrictedAsset: false }
    : !publicClient || probeQuery.isError
      ? { status: 'error' }
      : probeQuery.data !== undefined
        ? { status: 'success', hasRestrictedAsset: probeQuery.data }
        : { status: 'pending' }
  const verdict = getMarketGeoRestrictionVerdict(countryCode, chainId, probe)
  const { isPending, isRestricted } = verdict

  return { isPending, isRestricted, message: MARKET_GEO_RESTRICTION_MESSAGE }
}
